/**
 * Halloween posters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenPostersPack: ToilePack = {
    slug: 'halloween-posters',
    category: 'season',
    name: 'Halloween posters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pumpkin', label: 'Big pumpkin', subject: 'A huge carved pumpkin filling the poster with a jagged grin, a bat above, no text'},
        {key: 'house', label: 'Haunted house', subject: 'A haunted house on a hill under a full moon with bats, no text'},
        {key: 'greeting', label: 'Greeting', subject: 'A poster with "HAPPY HALLOWEEN" lettered large in drippy letters with a web border and nothing else'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ["for a kids' party", 'in a single color', 'as a smaller flyer'],
    directionHint: "try: for a kids' party instead",
    seo: {
        title: 'Halloween Posters: Pumpkin, House, Greeting Art',
        metaDescription: 'Nine halloween posters -- big pumpkin, haunted house, greeting -- in spooky cute, vintage, and bold graphic finishes. Bold, street-readable.',
        h1: 'Halloween poster design: big pumpkin, haunted house, and greeting',
        tagline: 'Nine posters, three window designs, three finishes each.',
        intro: "Nine street-readable halloween poster designs: a huge carved pumpkin, a haunted house under a full moon, and a 'Happy Halloween' greeting in drippy lettering with a web border. Three finishes -- spooky cute, vintage, and bold graphic -- for a shop window or front door. Ask for a kids' party version instead, or a single color, and the same three finishes carry it.",
        useCases: [
            'a storefront window poster',
            'a front door or porch sign',
            'a classroom party decoration',
            'a printable flyer',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine poster designs -- a big pumpkin, a haunted house, and a lettered greeting -- each in spooky cute, vintage, and bold graphic finishes, bold enough to read from the street.'},
            {q: 'What size does this print at?', a: 'The posters render at a tall portrait ratio, matching a standard 18x24in or smaller flyer size -- scale to your printer.'},
            {q: "Is this appropriate for a kids' event?", a: "The spooky cute finish is the gentlest of the three; the direction field can also retarget any design for a kids' party."},
            {q: "Can I get a different design, like for a kids' party?", a: "Yes -- the direction field takes any variation, including for a kids' party instead, a single color, or a smaller flyer size."},
        ],
    },
}
